import { QUESTION_TYPE } from '../../fetchSurvey/constants/questionTypes';
import type { Survey, SurveyPage, SurveyQuestion } from '../../fetchSurvey/types';
import type { McqQuestion } from '../../fetchSurvey/types/mcq';
import type { SurveyPaginationRefs } from '../../surveyPagination/types';
import { simulateActivePath } from '../../surveyProgress/utils/activePath';
import { evaluateDisplayLogic } from '../evaluator/evaluateDisplayLogic';
import { resolveEndPageResolution } from '../endPages/resolveEndPageId';
import {
  logAnswerLogicMatch,
  logDisplayLogicMatch,
  logEndPageLogicMatch,
  logQuestionVariantMatch,
} from '../logger/logicLogger';
import type { LogicContext } from '../types';
import {
  trackAnswerLogicMatched,
  trackDisplayLogicMatched,
  trackEndPageLogicMatched,
  trackQuestionVariantMatched,
} from '../telemetry/logicMatchTelemetry';
import { isQuestionVisible } from '../visibility/questionVisibility';
import type { SurveyAnswers } from '../../submitSurvey/types';
import type { CustomFieldValueMap } from '../types/customFieldValues';
import { isAnswerLogicVisible } from '../visibility/visibleItems';
import { getScaleColumns, getStatementRows, hasScaleColumnAnswerLogic } from '../visibility/questionAccess';

export function findPageIndexForQuestion(pages: SurveyPage[], questionId: string): number {
  return pages.findIndex(page => page.questions.some(question => question.id === questionId));
}

function collectQuestionsOnPath(
  pages: SurveyPage[],
  pathIndices: number[]
): SurveyQuestion[] {
  const questions: SurveyQuestion[] = [];
  for (const pageIndex of pathIndices) {
    const page = pages[pageIndex];
    if (!page) continue;
    questions.push(...page.questions);
  }
  return questions;
}

function collectAnswerLogicEntityKeys(
  question: SurveyQuestion,
  context: LogicContext
): Set<string> {
  const keys = new Set<string>();

  if (question.type === QUESTION_TYPE.MCQ) {
    const mcq = question as McqQuestion;
    for (const option of mcq.options) {
      if (option.answerLogic && isAnswerLogicVisible(option.answerLogic, context)) {
        keys.add(`${question.id}:option:${option.id}`);
      }
    }
    return keys;
  }

  for (const row of getStatementRows(question)) {
    if (row.answerLogic && isAnswerLogicVisible(row.answerLogic, context)) {
      keys.add(`${question.id}:row:${row.id}`);
    }
  }

  if (hasScaleColumnAnswerLogic(question.type)) {
    for (const column of getScaleColumns(question)) {
      if (column.answerLogic && isAnswerLogicVisible(column.answerLogic, context)) {
        keys.add(`${question.id}:column:${column.id}`);
      }
    }
  }

  return keys;
}

function resolveMatchedVariant(
  question: SurveyQuestion,
  context: LogicContext
): { variantId: string; variantText: string } | null {
  const variants = question.displayVariants;
  if (!variants?.length) return null;

  for (const variant of variants) {
    if (variant.displayLogic && evaluateDisplayLogic(variant.displayLogic, context)) {
      return { variantId: variant.id, variantText: variant.questionText };
    }
  }

  return null;
}

function parseAnswerEntityKey(key: string): {
  questionId: string;
  entityType: 'option' | 'row' | 'column';
  entityId: string;
} | null {
  const parts = key.split(':');
  if (parts.length !== 3) return null;

  const [questionId, entityType, entityId] = parts;
  if (entityType !== 'option' && entityType !== 'row' && entityType !== 'column') {
    return null;
  }

  return { questionId, entityType, entityId };
}

export type ObserveLogicMatchTransitionsInput = {
  survey: Survey;
  pages: SurveyPage[];
  allQuestions: SurveyQuestion[];
  previousValues: SurveyAnswers;
  nextValues: SurveyAnswers;
  customFieldValues: CustomFieldValueMap;
  previousContext: LogicContext;
  nextContext: LogicContext;
  refs: SurveyPaginationRefs;
  triggerQuestionId: string;
};

export function observeLogicMatchTransitions(input: ObserveLogicMatchTransitionsInput): void {
  const {
    survey,
    pages,
    allQuestions,
    previousValues,
    nextValues,
    customFieldValues,
    previousContext,
    nextContext,
    refs,
    triggerQuestionId,
  } = input;

  const previousPath = simulateActivePath(pages, previousValues, allQuestions, customFieldValues);
  const nextPath = simulateActivePath(pages, nextValues, allQuestions, customFieldValues);
  const pathIndices = [...new Set([...previousPath, ...nextPath])].sort((a, b) => a - b);
  const pathQuestions = collectQuestionsOnPath(pages, pathIndices);

  for (const question of pathQuestions) {
    if (!question.displayLogic) continue;

    const wasVisible = isQuestionVisible(question, previousContext);
    const nowVisible = isQuestionVisible(question, nextContext);
    if (wasVisible || !nowVisible) continue;

    const pageIndex = findPageIndexForQuestion(pages, question.id);
    logDisplayLogicMatch(question.id, pageIndex, triggerQuestionId);
    trackDisplayLogicMatched(refs, {
      questionId: question.id,
      pageIndex,
      triggerQuestionId,
    });
  }

  for (const question of pathQuestions) {
    const previousKeys = collectAnswerLogicEntityKeys(question, previousContext);
    const nextKeys = collectAnswerLogicEntityKeys(question, nextContext);
    const pageIndex = findPageIndexForQuestion(pages, question.id);

    for (const key of nextKeys) {
      if (previousKeys.has(key)) continue;

      const parsed = parseAnswerEntityKey(key);
      if (!parsed) continue;

      logAnswerLogicMatch(parsed.questionId, parsed.entityType, parsed.entityId, pageIndex, triggerQuestionId);
      trackAnswerLogicMatched(refs, {
        questionId: parsed.questionId,
        entityType: parsed.entityType,
        entityId: parsed.entityId,
        pageIndex,
        triggerQuestionId,
      });
    }
  }

  for (const question of pathQuestions) {
    if (!question.displayVariants?.length) continue;

    const previousVariant = resolveMatchedVariant(question, previousContext);
    const nextVariant = resolveMatchedVariant(question, nextContext);
    const previousKey = previousVariant?.variantId ?? 'base';
    const nextKey = nextVariant?.variantId ?? 'base';

    refs.lastResolvedVariantByQuestionId.set(question.id, nextKey);

    if (nextKey === previousKey || !nextVariant) continue;

    logQuestionVariantMatch(
      question.id,
      nextVariant.variantId,
      nextVariant.variantText,
      triggerQuestionId
    );
    trackQuestionVariantMatched(refs, {
      questionId: question.id,
      variantId: nextVariant.variantId,
      variantText: nextVariant.variantText,
      triggerQuestionId,
    });
  }

  const previousEndPage = resolveEndPageResolution(survey, previousContext);
  const nextEndPage = resolveEndPageResolution(survey, nextContext);

  if (nextEndPage.endPageId && nextEndPage.endPageId !== refs.lastResolvedEndPageId) {
    refs.endPageChangeCount += 1;
    refs.lastResolvedEndPageId = nextEndPage.endPageId;

    logEndPageLogicMatch(
      nextEndPage.endPageId,
      nextEndPage.matchType,
      previousEndPage.endPageId,
      triggerQuestionId
    );
    trackEndPageLogicMatched(refs, {
      endPageId: nextEndPage.endPageId,
      matchType: nextEndPage.matchType,
      previousEndPageId: previousEndPage.endPageId,
      triggerQuestionId,
    });
  }
}
